import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
    BookText, FolderKanban, ShieldCheck, Network, ListChecks, Lightbulb,
    Gauge, Swords, FileDown, Share2, Terminal, ArrowRight, Zap, Globe2,
    BookMarked, UploadCloud, GitCompareArrows, GitBranch,
    KeyRound, Search, X, Bot, CalendarClock,
} from "lucide-react";
import { Panel } from "@/components/common/Panel";
import { TiltCard } from "@/components/common/TiltCard";
import { AnimatedNumber } from "@/components/common/AnimatedNumber";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { PipelineIntro } from "@/components/docs/PipelineIntro";
import { staggerContainer, staggerItem } from "@/lib/motion";
import { TID } from "@/lib/testIds";

const BY_THE_NUMBERS = [
    { label: "Real SoD Risk Rules", value: 258, sub: "SAP GRC Access Control export" },
    { label: "Critical Activities", value: 26, sub: "standalone sensitive functions" },
    { label: "Real Fiori Apps", value: 17226, sub: "offline reference library" },
    { label: "Data-Driven Role Templates", value: 30, sub: "23+ SAP modules + P2P/O2C" },
    { label: "Mitigation Controls", value: 28, sub: "real-time SoD remediation" },
    { label: "Governance Frameworks", value: 10, sub: "SOX, GDPR, JSOX & more" },
];

const PIPELINE = [
    { icon: UploadCloud, t: "Upload Artifacts", d: "ABAP, SU24/SU22, OData EDMX — or paste raw text" },
    { icon: ShieldCheck, t: "Static Engines Analyze", d: "Authorization, SU24 & OData engines, fully offline" },
    { icon: Network, t: "Findings + Knowledge Graph", d: "Every issue linked, evidenced and visualized" },
    { icon: Lightbulb, t: "AI Recommendations", d: "UTSAVA Reasoner, explainable" },
    { icon: Swords, t: "Engineer Roles", d: "Sentinel Forge: T-code, Fiori Catalog or template roles, field-verified SoD" },
    { icon: GitBranch, t: "Diagram It", d: "Diagram Studio: SAP templates rendered as vector SVG/PNG, AI-refinable" },
    { icon: Globe2, t: "Govern & Mitigate", d: "Frameworks mapped, controls suggested live" },
    { icon: Bot, t: "Ask Utsav", d: "Talk or type — the AI agent can act on your workspace directly" },
    { icon: Share2, t: "Share & Export", d: "Branded links, PDF/DOCX/XLSX/.SAP deliverables" },
];

const CHAPTERS = [
    {
        icon: KeyRound, title: "1 · Getting Started", to: "/",
        steps: [
            "Sign in with your credentials — access is role-gated (admin, architect, consultant, auditor).",
            "Your role determines which engines and modules you can execute; ask an admin to grant additional roles from the Admin console, or a Dynamic RBAC custom role scoped to specific modules.",
            "Land on the Executive Overview: workspace KPIs, recent activity, and the SoD Conflict Heatmap across your entire role library.",
        ],
    },
    {
        icon: FolderKanban, title: "2 · Workspaces & Artifact Upload", to: "/workspaces",
        steps: [
            "Create a new workspace — each one is an isolated, offline engineering project.",
            "Upload exported SAP artifacts: ABAP source, SU24/SU22 proposals, or OData EDMX metadata — or use Paste Text for quick ad-hoc analysis.",
            "Nothing leaves the workspace boundary; all parsing happens locally, no live SAP connection is ever made. Remove any artifact you no longer need with the trash icon.",
        ],
    },
    {
        icon: ShieldCheck, title: "3 · Run Analysis", to: "/workspaces",
        steps: [
            "Trigger analysis — the Authorization, SU24 and OData engines statically inspect every artifact.",
            "Each run automatically saves a timeline snapshot (KPIs, severity & category counts) — this powers Timeline Diff later.",
            "Findings, recommendations and a Knowledge Graph are generated in the same pass.",
        ],
    },
    {
        icon: ListChecks, title: "4 · Findings & Recommendations", to: "/findings",
        steps: [
            "Review every detected issue with severity, evidence, affected objects and rule references.",
            "Filter by severity, engine or status; drill into any finding for full context.",
            "Accept or reject AI-assisted recommendations per your own governance workflow — nothing auto-applies.",
            "Every approval or rejection now captures a Change Impact Brief automatically: the blast radius of that change through the Knowledge Graph, frozen at decision time and downloadable as a PDF from the recommendation drawer — so each decided change carries its own audit evidence.",
        ],
    },
    {
        icon: Network, title: "5 · Knowledge Graph", to: "/workspaces",
        steps: [
            "Open any workspace's Knowledge Graph tab to see artifacts → programs → authorization objects → findings → recommendations as a live network.",
            "Rendered over a 3D parallax depth field — pan, zoom, and click any node to inspect it.",
        ],
    },
    {
        icon: Gauge, title: "6 · Risk, Compliance & Clean Core", to: "/workspaces",
        steps: [
            "Executive posture scores — risk, confidence, Clean-Core alignment — recompute on every analysis run.",
            "Use Compare → Cross-Workspace Diff to benchmark two workspaces, or Timeline Diff to see how ONE workspace evolved analysis-over-analysis.",
        ],
    },
    {
        icon: Swords, title: "7 · Role Engineering — Sentinel Forge", to: "/roles",
        steps: [
            "Role Library tab: mass-generate PFCG roles from specs, browse the real 258-rule SoD Rulebook (Admin-only detail; every rule and Function is tagged GRC EXPORT or ENRICHED so you always know its provenance), run within-role and cross-role SoD simulation, consolidate redundant roles. Each role shows a Fiori Catalog / T-code-only coverage badge.",
            "Fiori Apps Library tab: search all 17,226 real SAP Fiori apps by module or S/4HANA release version — fully offline.",
            "Fiori Catalog Roles tab: search real Fiori Business Catalogs, save a reusable multi-catalog \"Fiori Group\", then engineer a role directly from selected catalogs/groups — every resolved app's T-codes run through the same Secure-by-Design SoD pipeline as a T-code role.",
            "Role Templates tab: one-click generate any of the 30 data-driven templates (23+ real SAP modules + Procure-to-Pay/Order-to-Cash composites) with SoD analysis pre-computed.",
            "Per-role Tools (wrench icon) → Authorization Matrix Manager: Org-Level Field mapping, a Field Editor to capture REAL ACTVT values (which can confirm or clear a conflict), Simulate Addition, Release Readiness, AI-generated BPD/PDD documents with in-app preview, and a \"Fiori Migration\" tab that one-click converts an existing T-code role onto the smallest covering set of real Fiori Catalogs.",
            "Every role can be exported as a byte-accurate PFCG-style .SAP reference file — catalog-based roles additionally include the Fiori launchpad menu hierarchy (AGR_HIER/AGR_HIERT).",
        ],
    },
    {
        icon: GitBranch, title: "8 · SAP Diagram Studio", to: "/roles",
        steps: [
            "Open any role's Tools (wrench icon) → Diagrams tab to enter the Diagram Studio — a deterministic, text-driven engine, so every T-code and authorization object stays crisp, selectable and searchable (no blurry AI raster images).",
            "Choose an SAP template: Three-Tier Role Model, SoD Conflict Matrix, Authorization Concept, or the Procure-to-Pay / Order-to-Cash process flows — each is rendered from your real role data, with SoD-conflicted transactions colour-coded.",
            "Refine iteratively in plain English (\"collapse the org levels\", \"add the approval control\") — the UTSAVA Reasoner rewrites the diagram while keeping it valid and renderable.",
            "Export as vector SVG or high-resolution PNG; every generation is versioned into that role's Diagram History so you can restore an earlier version at any time.",
            "On the Role Library's cross-role simulation, click \"Visualize as Diagram\" to render toxic role combinations as a cross-role SoD matrix. Generated diagrams are also embedded into the BPD / PDD .docx deliverables.",
        ],
    },
    {
        icon: Zap, title: "9 · Instant Analysis", to: "/instant-analysis",
        steps: [
            "Type any T-code or Fiori App ID for an immediate, offline lookup.",
            "Get the real matched Fiori app, any Segregation-of-Duties risks from the 258-rule ruleset, standalone critical activities, inferred authorization objects, and a UTSAVA Reasoner executive summary.",
            "Below the single lookup: the Instant SoD Analyser lets you multi-select existing Roles plus ad-hoc T-codes/apps/objects to simulate a user holding all of them, exportable to XLSX/PDF.",
        ],
    },
    {
        icon: Bot, title: "10 · Utsav — The AI Agent", to: "/utsav",
        steps: [
            "Chat or speak with Utsav — pick UTSAVA Reasoner Deep (deeper reasoning) or Fast (fastest replies) from the engine selector.",
            "Utsav can act on the app itself: look up a T-code/Fiori app, run an ad-hoc SoD simulation, list or describe your roles, draft a BPD/PDD, or generate an image — all via live tool-calling, shown as it happens.",
            "Tap the mic to speak (Whisper transcription) and enable Auto-speak for spoken replies (OpenAI TTS); the prominent Stop button halts generation AND audio instantly.",
            "Utsav remembers facts across sessions (Brain icon) and keeps a session sidebar so you can resume any past conversation.",
        ],
    },
    {
        icon: Globe2, title: "11 · Governance Map & Mitigation Library", to: "/governance",
        steps: [
            "Explore the interactive 3D world map of 10 global compliance frameworks (SOX, GDPR, JSOX and more) anchored to their regions.",
            "Browse the 28-control Mitigation Library — controls are suggested automatically wherever an SoD conflict or critical activity is detected.",
        ],
    },
    {
        icon: GitCompareArrows, title: "12 · Compare", to: "/compare",
        steps: [
            "Cross-Workspace Diff: pick any two workspaces to see the KPI delta, severity distribution and category-level changes.",
            "Timeline Diff: pick ONE workspace and two of its own analysis-run snapshots to see how its posture changed over time.",
        ],
    },
    {
        icon: FileDown, title: "13 · Reports & Exports", to: "/workspaces",
        steps: [
            "Export any workspace analysis to PDF, DOCX, XLSX or PPTX — audit-ready, generated entirely offline.",
            "Export the full role catalog, or an individual role's PFCG-style .SAP reference file and AI-generated BPD/PDD documentation.",
        ],
    },
    {
        icon: Share2, title: "14 · Share Hub", to: "/share",
        steps: [
            "Generate branded, masked short links (on your own domain) with QR codes for the Genesis Tool or Admin Console.",
            "Track link usage and revoke access at any time from the Share Hub.",
        ],
    },
    {
        icon: CalendarClock, title: "15 · Weekly Posture Digest (Admin)", to: "/admin/digest",
        steps: [
            "One scheduled bundle of everything that moved in the week: new findings by severity and engine, every approval/rejection and whether it carried its Change Impact Brief, the most toxic roles, active ruleset coverage and RICEFW clearance verdicts.",
            "Set the weekly slot (UTC day + hour) and the look-back window, or press Generate now for an on-demand digest — the scheduler still produces a missed week on its next run rather than skipping it.",
            "Every digest is kept in history with week-over-week deltas and exports to a board-ready PDF. Generated fully offline — nothing is emailed or sent anywhere.",
        ],
    },
];

export default function Documentation() {
    const nav = useNavigate();
    const [search, setSearch] = useState("");

    const filteredChapters = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return CHAPTERS;
        return CHAPTERS.filter((c) =>
            c.title.toLowerCase().includes(q) || c.steps.some((s) => s.toLowerCase().includes(q))
        );
    }, [search]);
    return (
        <div className="space-y-6">
            {/* Hero */}
            <div className="panel-elevated relative overflow-hidden p-6">
                <div className="absolute -right-10 -top-10 w-56 h-56 rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />
                <div className="flex items-center gap-4 relative">
                    <div className="w-12 h-12 rounded-sm bg-gradient-to-br from-cyan-300 to-cyan-600 flex items-center justify-center shadow-[0_0_20px_rgb(var(--accent-1-rgb) / 0.45)]">
                        <BookText size={22} className="text-slate-950" strokeWidth={2.5} />
                    </div>
                    <div>
                        <h1 className="font-display text-2xl text-white tracking-wide">Platform Guide Book</h1>
                        <p className="text-sm text-slate-400 mt-0.5">
                            UTSAVA Genesis <span className="text-cyan-400">- um-alphabydesign</span> · offline SAP security engineering, cover to cover
                        </p>
                    </div>
                </div>
            </div>

            {/* By the numbers — infographic strip, all real, live counts */}
            <div>
                <div className="label-eyebrow mb-3">By The Numbers</div>
                <motion.div variants={staggerContainer} initial="initial" animate="animate" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    {BY_THE_NUMBERS.map((n) => (
                        <motion.div key={n.label} variants={staggerItem}>
                            <TiltCard max={6} className="group panel-elevated card-lift p-4 rounded-[4px] h-full">
                                <div className="font-display text-2xl text-cyan-300" style={{ transform: "translateZ(24px)" }}>
                                    <AnimatedNumber value={n.value} />
                                </div>
                                <div className="text-xs text-white font-medium mt-1" style={{ transform: "translateZ(16px)" }}>{n.label}</div>
                                <div className="text-[10px] text-slate-500 font-mono mt-0.5">{n.sub}</div>
                            </TiltCard>
                        </motion.div>
                    ))}
                </motion.div>
            </div>

            {/* Pipeline infographic — self-playing guided intro for first-time visitors */}
            <Panel eyebrow="Infographic" title="How UTSAVA Genesis Works">
                <PipelineIntro steps={PIPELINE} />
            </Panel>

            {/* Step-by-step guide book */}
            <Panel
                eyebrow="Guide Book"
                title="Every module, step by step"
                action={
                    <div className="relative">
                        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
                        <input
                            data-testid={TID.docSearchInput}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search chapters…"
                            className="w-[220px] bg-[var(--bg-base)] border border-white/10 rounded-sm pl-8 pr-7 py-1.5 text-xs font-mono text-slate-200 focus:border-cyan-500/40 focus:outline-none"
                        />
                        {search && (
                            <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-cyan-300">
                                <X size={12} />
                            </button>
                        )}
                    </div>
                }
            >
                {filteredChapters.length === 0 ? (
                    <p className="text-sm text-slate-500 font-mono py-6 text-center">No chapters match &ldquo;{search}&rdquo;.</p>
                ) : (
                <Accordion type="single" collapsible className="w-full">
                    {filteredChapters.map((c) => (
                        <AccordionItem key={c.title} value={c.title} className="border-white/10">
                            <AccordionTrigger data-testid={TID.docChapterTrigger(c.title)} className="text-slate-200 hover:text-cyan-300 hover:no-underline">
                                <span className="flex items-center gap-2.5">
                                    <c.icon size={15} className="text-cyan-400 shrink-0" />
                                    <span className="font-display text-sm">{c.title}</span>
                                </span>
                            </AccordionTrigger>
                            <AccordionContent>
                                <ol className="space-y-2 ml-1">
                                    {c.steps.map((s, i) => (
                                        <li key={i} className="flex items-start gap-3 text-sm text-slate-400">
                                            <span className="mt-0.5 shrink-0 w-5 h-5 rounded-sm bg-cyan-500/10 text-cyan-300 border border-cyan-500/25 flex items-center justify-center font-mono text-[10px]">{i + 1}</span>
                                            {s}
                                        </li>
                                    ))}
                                </ol>
                                <button
                                    data-testid={TID.docChapterOpenBtn(c.title)}
                                    onClick={() => nav(c.to)}
                                    className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-mono text-cyan-400 hover:text-cyan-300 transition-colors"
                                >
                                    Open this module <ArrowRight size={11} />
                                </button>
                            </AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
                )}
            </Panel>

            {/* Principles */}
            <Panel eyebrow="Design Principles" title="Offline-first · Explainable · Secure-by-Design">
                <div className="grid md:grid-cols-3 gap-4">
                    {[
                        { icon: Terminal, t: "Offline-first", d: "No live SAP connection. Everything runs against exported artifacts and bundled reference data, so nothing sensitive leaves the workspace." },
                        { icon: Lightbulb, t: "Explainable", d: "Every finding, template and conflict carries evidence, references and rationale — no black-box scoring." },
                        { icon: ShieldCheck, t: "Secure-by-Design", d: "SoD conflicts and critical access are surfaced before roles are provisioned, not after an audit." },
                    ].map((p) => (
                        <div key={p.t} className="panel p-4">
                            <div className="flex items-center gap-2">
                                <p.icon size={15} className="text-cyan-400" />
                                <span className="text-sm text-white font-medium">{p.t}</span>
                            </div>
                            <p className="text-xs text-slate-400 mt-1.5 leading-relaxed">{p.d}</p>
                        </div>
                    ))}
                </div>
                <div className="mt-4 flex items-center gap-2 text-[11px] font-mono text-slate-500">
                    <BookMarked size={12} className="text-cyan-400" />
                    Need the full architecture? See the in-repo PRD maintained by the engineering team.
                </div>
            </Panel>
        </div>
    );
}
